import React, { useMemo, useState } from 'react'
import Map, { FullscreenControl, GeolocateControl, NavigationControl, ScaleControl, Source, Layer, Marker, Popup } from 'react-map-gl/maplibre';
import Pin from './pin';
import { useTranslation } from 'react-i18next';
import { usePositionedViewport } from '../../../hooks';

const MAX_ZOOM_LEVEL = 18;

const getHeatmapLayer = (max) => ({
    id: 'branchOffice-heatmap',
    maxzoom: MAX_ZOOM_LEVEL,
    type: 'heatmap',
    paint: {
        // Color ramp for heatmap.  Domain is 0 (low) to 1 (high).
        // Begin color ramp at 0-stop with a 0-transparancy color
        // to create a blur-like effect.
        'heatmap-color': [
            'interpolate',
            ['linear'],
            ['heatmap-density'],
            0,
            'rgba(252,229,30,0)',
            0.2,
            'rgb(132,212,50)',
            0.4,
            'rgb(38,162,108)',
            0.6,
            'rgb(33,106,123)',
            0.8,
            'rgb(48,52,117)',
            0.9,
            'rgb(52,0,66)'
        ],

        // Increase the heatmap weight based on a property
        'heatmap-weight': ['interpolate', ['linear'], ['get', 'count'], 0, 0, max / 2, 1, max, 2],

        // Increase the heatmap color weight weight by zoom level
        // heatmap-intensity is a multiplier on top of heatmap-weight
        'heatmap-intensity': ['interpolate', ['linear'], ['zoom'], 7, 1, 14, 2],

        // Adjust the heatmap radius by zoom level
        'heatmap-radius': ['interpolate', ['linear'], ['zoom'], 0, 2, 12, 40],

        // Transition from heatmap to circle layer by zoom level
        // when zoom level is 12 or below, the opacity will be 1
        // when zoom level is 18 or above, the opacity will be 0
        'heatmap-opacity': ['interpolate', ['linear'], ['zoom'], 12, 1, MAX_ZOOM_LEVEL, 0]
    }
})

function BranchOfficeMap(props) {
    const { data } = props;
    const { t } = useTranslation('clients-dashboard', { keyPrefix: 'allClientsScreen' });

    const viewport = usePositionedViewport();
    const [popupInfo, setPopupInfo] = useState(null);

    const { geoJsonData, heatmapLayer } = useMemo(() => {
        if (!data || !data.length) return {};

        let max = 0;
        const geojson = {
            type: "FeatureCollection",
            features: data.map((item) => {
                if (item.count > max) {
                    max = item.count;
                }

                if (!item.coordinates) return {}

                return {
                    type: "Feature",
                    geometry: {
                        type: "Point",
                        coordinates: [Number(item.coordinates.long), Number(item.coordinates.lat)]
                    },
                    properties: {
                        id: item.id,
                        name: item.name,
                        count: item.count
                    }
                }
            })
        }
        return {
            geoJsonData: geojson,
            heatmapLayer: getHeatmapLayer(max)
        }
    }, [data])

    const markers = useMemo(
        () =>
            data.map((branchOffice) => branchOffice.coordinates ? (
                <Marker
                    key={`marker-${branchOffice.id}`}
                    longitude={+branchOffice.coordinates.long}
                    latitude={+branchOffice.coordinates.lat}
                    anchor="bottom"
                    onClick={e => {
                        e.originalEvent.stopPropagation();
                        setPopupInfo(branchOffice);
                    }}
                >
                    <Pin />
                </Marker>
            ) : null),
        [data]
    );

    if (!viewport) return <></>

    return (
        <Map
            initialViewState={viewport}
            style={{ height: "100%" }}
            mapStyle="https://api.maptiler.com/maps/basic-v2/style.json?key=zQZLhSau046UB7IrjeYK"
            interactiveLayerIds={['branchOffice-heatmap']}
            reuseMaps
        >
            <GeolocateControl position="top-left" showAccuracyCircle={false} positionOptions={{ enableHighAccuracy: true }} fitBoundsOptions={{ maxZoom: 10 }} />
            <FullscreenControl position="top-left" />
            <NavigationControl position="top-left" />
            <ScaleControl />

            {geoJsonData && (
                <Source type="geojson" data={geoJsonData}>
                    <Layer {...heatmapLayer} />
                </Source>
            )}

            {markers}

            {popupInfo && (
                <Popup
                    anchor="top"
                    longitude={Number(popupInfo.coordinates.long)}
                    latitude={Number(popupInfo.coordinates.lat)}
                    onClose={() => setPopupInfo(null)}
                >
                    <p><strong>{popupInfo.name}</strong></p>
                    <p>{popupInfo.count} {t("sales")}</p>
                </Popup>
            )}
        </Map >
    )
}

export default BranchOfficeMap